import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { IInputProps } from 'modules';
import { PopupWraper } from '../../popup';
import './styles.scss';

export interface SearchSelectOption {
  label: string;
  value?: string | number;
  logo?: string;
  searchText?: string;
}

export interface InputSearchSelectProps extends IInputProps {
  options?: readonly SearchSelectOption[];
  isClearable?: boolean;
  isLoading?: boolean;
  title?: string;
}

const normalizeSearch = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .trim();

// Controlled input: options are loaded and mapped by the caller.
export const InputSearchSelect = ({
  name,
  value,
  onChange,
  onTouched,
  onBlur,
  onFocus,
  options = [],
  isDisabled,
  isReadOnly,
  isClear,
  isClearable = true,
  isLoading = false,
  placeholder = 'Chọn mục',
  title = placeholder,
  label,
  error,
  tabIndex,
}: InputSearchSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const disabled = isDisabled || isReadOnly;
  const selected = options.find((option) => option.value === value);
  const hasValue = value !== undefined && value !== null && value !== '';
  const showClear = !disabled && hasValue && (isClear ?? isClearable);
  const filteredOptions = useMemo(() => {
    const query = normalizeSearch(search);
    return options.filter((option) =>
      normalizeSearch(
        `${option.label} ${option.value ?? ''} ${option.searchText ?? ''}`,
      ).includes(query),
    );
  }, [options, search]);

  useEffect(() => {
    if (!isOpen) return;
    searchRef.current?.focus();
    const trigger = triggerRef.current;
    return () => {
      trigger?.focus();
    };
  }, [isOpen]);

  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);

  const close = () => {
    setIsOpen(false);
    onTouched?.();
  };

  const handleDialogKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (event.key === 'Enter' && event.target === searchRef.current) {
      event.preventDefault();
    } else if (event.key === 'Tab') {
      const controls = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not(:disabled), input:not(:disabled)',
      );
      if (!controls?.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  return (
    <div className='InputSearchSelect'>
      <button
        ref={triggerRef}
        type='button'
        name={name}
        className={`InputSearchSelect__trigger${showClear ? ' InputSearchSelect__trigger--clearable' : ''}`}
        disabled={disabled}
        tabIndex={tabIndex}
        aria-label={typeof label === 'string' ? label : placeholder}
        aria-haspopup='dialog'
        aria-expanded={isOpen}
        aria-invalid={!!error}
        onFocus={onFocus}
        onBlur={() => {
          onBlur?.();
          if (!isOpen) onTouched?.();
        }}
        onClick={() => {
          setSearch('');
          setIsOpen(true);
        }}
      >
        {selected?.logo && (
          <img src={selected.logo} alt='' className='InputSearchSelect__logo' />
        )}
        <span
          className={
            hasValue
              ? 'InputSearchSelect__label'
              : 'InputSearchSelect__placeholder'
          }
        >
          {selected?.label || (hasValue ? String(value) : placeholder)}
        </span>
        <svg
          className='InputSearchSelect__chevron'
          width='20'
          height='20'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
          aria-hidden='true'
          focusable='false'
        >
          <path d='m6 9 6 6 6-6' />
        </svg>
      </button>
      {showClear && (
        <button
          type='button'
          className='InputSearchSelect__clear'
          aria-label='Xóa lựa chọn'
          onClick={() => {
            onChange(undefined);
            onTouched?.();
            triggerRef.current?.focus();
          }}
        >
          ×
        </button>
      )}
      {isOpen &&
        !disabled &&
        createPortal(
          <div ref={dialogRef} onKeyDown={handleDialogKeyDown}>
            <PopupWraper
              title={title}
              onClose={close}
              className='InputSearchSelect__modal'
              style={{ maxWidth: 720 }}
            >
              <input
                ref={searchRef}
                type='search'
                className='InputSearchSelect__search'
                aria-label='Tìm kiếm'
                placeholder='Tìm theo tên hoặc mã'
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <div className='InputSearchSelect__options' aria-busy={isLoading}>
                {isLoading ? (
                  <p role='status'>Đang tải dữ liệu...</p>
                ) : filteredOptions.length ? (
                  filteredOptions.map((option) => (
                    <button
                      type='button'
                      key={option.value}
                      className='InputSearchSelect__option'
                      aria-pressed={option.value === value}
                      onClick={() => {
                        onChange(option.value);
                        close();
                      }}
                    >
                      {option.logo && (
                        <img
                          src={option.logo}
                          alt=''
                          loading='lazy'
                          className='InputSearchSelect__logo'
                        />
                      )}
                      <span className='InputSearchSelect__label'>
                        {option.label}
                      </span>
                      {option.value === value && (
                        <span aria-hidden='true'>✓</span>
                      )}
                    </button>
                  ))
                ) : (
                  <p role='status'>Không tìm thấy kết quả</p>
                )}
              </div>
            </PopupWraper>
          </div>,
          document.body,
        )}
    </div>
  );
};
